import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { auth } from './firebase';

/**
 * Giriş yapar
 * @param email - Kullanıcı e-posta
 * @param password - Kullanıcı şifre
 * @returns Promise that resolves to the user credentials
 */
export const login = async (email: string, password: string) => {
  if (!auth) throw new Error('Kimlik doğrulama sistemi başlatılamadı');
  
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential;
  } catch (error: any) {
    console.error('Giriş sırasında hata:', error);
    if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      throw new Error('E-posta veya şifre hatalı');
    } else if (error.code === 'auth/too-many-requests') {
      throw new Error('Çok fazla başarısız giriş denemesi. Lütfen daha sonra tekrar deneyin');
    } else {
      throw new Error('Giriş yapılamadı: ' + (error.message || 'Bilinmeyen hata'));
    }
  }
};

/**
 * Çıkış yapar
 */
export const logout = async () => {
  if (!auth) throw new Error('Kimlik doğrulama sistemi başlatılamadı');
  
  try {
    await signOut(auth);
    persistSession(false);
  } catch (error) {
    console.error('Çıkış sırasında hata:', error);
    throw error;
  }
};

/**
 * Oturum durumunu izler
 * @param callback - Oturum durumu değiştiğinde çağrılacak callback fonksiyonu
 * @returns Unsubscribe function
 */
export const subscribeToAuthChanges = (
  callback: (user: User | null) => void
) => {
  if (!auth) {
    console.error('Kimlik doğrulama sistemi başlatılamadı');
    return () => {};
  }
  
  return onAuthStateChanged(auth, callback);
};

/**
 * Mevcut kullanıcıyı döndürür
 * @returns Current authenticated user or null
 */
export const getCurrentUser = () => {
  return auth?.currentUser || null;
};

/**
 * Oturum durumunu yerel depolamadan kontrol eder
 * @returns Boolean indicating if user has a persisted session
 */
export const hasPersistedSession = () => {
  return localStorage.getItem('auth_session') === 'true';
};

/**
 * Oturum durumunu yerel depolamaya kaydeder
 * @param hasSession - Boolean indicating if user has a session
 */
export const persistSession = (hasSession: boolean) => {
  if (hasSession) {
    localStorage.setItem('auth_session', 'true');
  } else {
    localStorage.removeItem('auth_session');
  }
};

/**
 * Higher-order function that wraps a service function with authentication check
 * @param serviceFunction - The service function to wrap with authentication
 * @returns A new function that performs authentication check before calling the service function
 */
export function withAuthentication<T extends any[], R>(
  serviceFunction: (...args: T) => Promise<R>
): (...args: T) => Promise<R> {
  return async (...args: T): Promise<R> => {
    if (!auth || !auth.currentUser) {
      throw new Error('User is not authenticated');
    }
    return serviceFunction(...args);
  };
} 